import classNames from 'classnames'

// components
import { Dropdown } from 'primereact/dropdown'
import { Button } from 'primereact/button'
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

// internal
import { useRecognitionForm } from '@aytm/app/stats/page/question-views/hm/components/controls/recognition-form/lib/hooks'
import { RECOGNITION_MODE_OPTIONS } from '@aytm/app/stats/page/question-views/hm/components/controls/lib/constants'

// styles
import style from '@aytm/app/stats/page/question-views/hm/components/controls/recognition-form/styles/RecognitionForm.module.scss'

const valueTemplate = option => {
  if (!option) {
    // the list has no search field, so the empty label invites a pick only
    return <span>Select...</span>
  }
  return (
    <>
      <i className="icon-aytmfd-bots" />
      <span dangerouslySetInnerHTML={{ __html: option.text }} />
    </>
  )
}

const itemTemplate = option => <span dangerouslySetInnerHTML={{ __html: option.text }} />

export const RecognitionForm = ({
  form,
  clusters,
  visualPosition,
  image,
  changeClustersForm,
  onChangeModel,
  onFetchCRUDKey,
  onUpdateData
}) => {
  const {
    handleCancelRecognition,
    handleRunRecognition,
    annotationType,
    setAnnotationType,
    error,
    isLoading,
    hasAnnotationsOfCurrentType,
    isEmpty
  } = useRecognitionForm({
    clusters,
    visualPosition,
    image,
    changeClustersForm,
    onChangeModel,
    onFetchCRUDKey,
    onUpdateData
  })

  const handleChangeFilter = event => {
    const { value } = event
    setAnnotationType(value)
  }

  return (
    <div className={style.form}>
      <div className={style.formFields}>
        <div className={style.formControls}>
          <Dropdown
            data-testid="stat-hm-recognition-type"
            className={classNames(style.select, 'p-dropdown-rounded')}
            aria-label="Recognition type"
            ariaLabel="Recognition type"
            value={annotationType}
            options={RECOGNITION_MODE_OPTIONS}
            optionValue="value"
            optionLabel="text"
            optionDisabled="disabled"
            fixedDropdownDir="bottom"
            disabled={form.isLoading || isLoading}
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            onChange={handleChangeFilter}
          />
          {/* a run in flight has nothing to press, so the spinner alone takes the button's place */}
          {form.isLoading || isLoading ? (
            <span className={style.runSpinner} role="status" aria-label="Recognition running">
              <i className="icon-aytmfd-refresh-v2 aytmfd-spin" />
            </span>
          ) : (
            <Button
              className={classNames(style.runButton, 'p-button-rounded', 'p-button-secondary')}
              label={hasAnnotationsOfCurrentType ? 'Rerun' : 'Run'}
              icon={<i className={classNames(style.runButtonIcon, 'icon-aytmfd-video-play')} />}
              iconPos="right"
              onClick={handleRunRecognition}
            />
          )}
          <Popup
            width="300px"
            trigger="click"
            direction="righttop"
            popupOffset={9}
            hideOtherPopupsOnShow
            content={() => (
              <p>
                This feature will automatically create clusters around the text, objects, or both elements in your
                uploaded image. Once clusters are created, you'll be able to adjust, hide, or remove them as you wish.
              </p>
            )}
          >
            <i className="icon-aytmfd-help-v2" />
          </Popup>
        </div>
        <div className={style.recognitionStatus}>
          {isLoading && (
            <div className={style.statusProgress}>
              Running content recognition. <b>This may take several minutes.</b> <b>Please do not close the window.</b>
            </div>
          )}
          {!isLoading && isEmpty && <div className={style.statusEmpty}>No clusters found.</div>}
        </div>
        <div className={style.controlItem}>
          <Button
            className={classNames(style.cancelButton, 'p-button-rounded', 'p-button-secondary')}
            label={isLoading ? 'Cancel' : 'Close'}
            onClick={handleCancelRecognition}
          />
        </div>
      </div>
      {error && <div className={style.formError}>{error}</div>}
    </div>
  )
}
